'use client'

import { CheckCircle2, Sparkles, Star, Target, XCircle } from 'lucide-react'

import { useLevelDetails } from '../hooks/useLevelDetails'
import { formatDateTime } from '../lib/content-history-labels'

import { Dialog } from '@/shared/ui/dialog'
import { Modal, ModalDescription, ModalTitle } from '@/shared/ui/Modal'

interface LevelDetailsModalProps {
  levelId: number | null
  levelName: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function LevelDetailsModal({
  levelId,
  levelName,
  open,
  onOpenChange
}: LevelDetailsModalProps) {
  const { record, isLoading } = useLevelDetails(open ? levelId : null)
  const challenges = record?.challenges ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Modal className="sm:max-w-lg">
        <ModalTitle className="flex items-center gap-2">
          <Sparkles className="size-5 text-primary" aria-hidden />
          {levelName ? `Nível — ${levelName}` : 'Detalhe do nível'}
        </ModalTitle>
        <ModalDescription>
          Sua pontuação neste nível e os desafios pontuáveis desta temporada.
        </ModalDescription>

        {isLoading || !record ? (
          <p className="py-8 text-center text-sm font-semibold text-muted-foreground">
            {isLoading ? 'Carregando detalhes…' : 'Não foi possível carregar o nível.'}
          </p>
        ) : (
          <div className="mt-2 flex flex-col gap-4">
            {/* Painel de pontuação (XP/reward → tom brand-yellow gamificado) */}
            <div className="flex items-center gap-4 rounded-2xl border-2 border-warning/40 bg-linear-to-t from-[#FFFBEA] to-white p-4 shadow-[0_4px_0_var(--color-warning)]">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-warning/15 text-warning">
                <Star className="size-6 fill-current" aria-hidden />
              </span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                  Pontuação adquirida
                </p>
                <p className="font-hl text-2xl font-extrabold leading-none text-primary-dark">
                  {record.acquired_point_reward}
                  <span className="text-lg font-bold text-muted-foreground">
                    {' / '}
                    {record.total_point_reward}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              <Target className="size-3.5" aria-hidden />
              Desafios pontuáveis
            </div>

            {challenges.length === 0 ? (
              <p className="rounded-2xl border-2 border-dashed border-border p-6 text-center text-sm font-semibold italic text-muted-foreground">
                Nenhum desafio pontuável concluído neste nível.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {challenges.map(challenge => (
                  <li
                    key={challenge.id}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-white/70 px-4 py-3 shadow-[0_2px_0_rgba(16,89,216,0.12)]"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-primary-dark">
                        {challenge.name ?? `#${challenge.challenge_id ?? '—'}`}
                      </p>
                      <p className="text-[11px] font-semibold text-muted-foreground">
                        {formatDateTime(challenge.completed_at)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {challenge.right_answer ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-success/12 px-2.5 py-1 text-xs font-bold text-success">
                          <CheckCircle2 className="size-3.5" aria-hidden />
                          Acertou
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-destructive/12 px-2.5 py-1 text-xs font-bold text-destructive">
                          <XCircle className="size-3.5" aria-hidden />
                          Errou
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 text-sm font-extrabold text-warning">
                        <Star className="size-3.5 fill-current" aria-hidden />
                        {challenge.point_reward}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Modal>
    </Dialog>
  )
}
